/**
 * Printer abstraction of the register (T07-25, design/05 §2/§6).
 *
 * The receipt content is **never** made on the tablet: pos-service renders the ESC/POS bytes
 * (T07-09 — raster Noto fonts, QR, cut) and the tablet only moves them to a printer:
 *
 * | driver | when | how |
 * |---|---|---|
 * | `webusb` | Android Chrome / SUNMI + USB cable (the simplest) | `navigator.usb` bulk OUT |
 * | `bluetooth` | Android Chrome + Bluetooth LE printer | `navigator.bluetooth` GATT write |
 * | `network` | iPad Safari (no WebUSB / Bluetooth) or a LAN printer | `infra/pos-print-bridge` on a mini PC → TCP 9100 |
 * | `mock` | tests / dev / no printer | keeps the jobs in memory |
 *
 * The cash drawer hangs on the printer (RJ11): `openDrawer()` sends the ESC/POS kick on its own
 * (a cash sale opens the drawer even when the receipt could not be printed).
 */
export type PrinterConnection = "webusb" | "bluetooth" | "network" | "mock" | "none";

export type PrinterErrorCode =
  | "not_supported" // this browser has no WebUSB / Web Bluetooth
  | "not_found" // no printer chosen / paired yet
  | "disconnected" // cable pulled / out of range / switched off
  | "write_failed"
  | "bridge_unreachable"
  | "timeout"
  | "no_printer"; // connection "none"

export class PrinterError extends Error {
  readonly code: PrinterErrorCode;

  constructor(code: PrinterErrorCode, message?: string) {
    super(message ?? code);
    this.name = "PrinterError";
    this.code = code;
  }
}

export interface PrinterStatus {
  connected: boolean;
  /** A human-readable device name (model) when known. */
  name?: string | null;
  error?: PrinterErrorCode | null;
}

export interface PrinterDriver {
  readonly kind: PrinterConnection;
  /** Is this driver usable in this browser at all? */
  supported(): boolean;
  /**
   * Choose / pair a printer. WebUSB and Web Bluetooth need a user gesture (a tap in settings) the
   * first time; later `print()` reconnects to the remembered device by itself.
   */
  connect(): Promise<PrinterStatus>;
  print(bytes: Uint8Array): Promise<void>;
  openDrawer(): Promise<void>;
  status(): Promise<PrinterStatus>;
  disconnect(): Promise<void>;
}

export function isPrinterError(value: unknown): value is PrinterError {
  return value instanceof PrinterError;
}
